import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getDashboard } from '../../services/employeeService';
import Preloader from '../../components/common/Preloader';
import { formatCurrency } from '../../utils/format';
import VerifyPasswordModal from '../../components/common/VerifyPasswordModal';

const EmployeePersonalInfo = () => {
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSalaryVisible, setIsSalaryVisible] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const navigate = useNavigate();

  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    if (isVerified) {
      loadData();
    }
  }, [isVerified]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await getDashboard();
      setEmployee(data.employee);
    } catch (err) {
      setError(err.message || 'Failed to load personal information');
    } finally {
      setLoading(false);
    }
  };

  const handleRevealSalary = () => {
    if (isSalaryVisible) {
      setIsSalaryVisible(false);
    } else {
      setShowVerifyModal(true);
    }
  };

  if (!isVerified) {
    return (
      <VerifyPasswordModal
        isOpen={true}
        onCancel={() => {
          navigate('/employee/dashboard');
        }}
        onClose={() => {
          // Do nothing on close, let onVerified handle success
        }}
        onVerified={() => {
          setIsVerified(true);
        }}
        message="Please enter your password to view your personal information."
      />
    );
  }

  if (loading) {
    return <Preloader />;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!employee) {
    return <div className="error-message">Information not available.</div>;
  }

  return (
    <div className="admin-employee-view" style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
      <div className="view-header" style={{ marginBottom: '2rem' }}>
        <div className="header-content">
          <h1 style={{ fontSize: '2rem', color: '#1e293b' }}>Personal Information</h1>
        </div>
      </div>

      <div className="dashboard-card info-card" style={{ padding: '2rem' }}>
        <div className="info-list" style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
          <div className="info-item profile-photo-section" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1.5rem', width: '100%' }}>
            <div style={{
              width: '120px', height: '120px', borderRadius: '50%', backgroundColor: '#e2e8f0',
              marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              overflow: 'hidden', border: '3px solid #cbd5e1'
            }}>
              {employee.profilePhoto ? (
                <img src={employee.profilePhoto} alt={employee.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <span style={{ fontSize: '4rem', color: '#94a3b8' }}>👤</span>
              )}
            </div>
            <h3 style={{ margin: '10px 0 5px 0', fontSize: '1.5rem', color: '#0f172a' }}>{employee.name}</h3>
            <span style={{ color: '#64748b', fontSize: '1rem' }}>{employee.designation || 'Employee'}</span>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Employee ID</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.employeeId}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Email</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.email}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Employee Type</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.employeeType || 'Full Time'}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Date of Joining</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>
                {employee.dateOfJoining ? new Date(employee.dateOfJoining).toLocaleDateString('en-GB').replace(/\//g, '.') : 'N/A'}
              </span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Work Hours / Day</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.workHours} hrs</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Basic Salary</span>
                <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>
                  {isSalaryVisible ? formatCurrency(employee.salary) : '******'}
                </span>
              </div>
              <button 
                onClick={handleRevealSalary}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: '#64748b' }}
                title={isSalaryVisible ? "Hide Salary" : "Show Salary"}
              >
                {isSalaryVisible ? '👁️‍🗨️' : '👁️'}
              </button>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Bank Name</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.bankName || 'N/A'}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>Account No</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.accountNo || 'N/A'}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>PAN Number</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.panNumber || 'N/A'}</span>
            </div>
            <div className="info-item" style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="label" style={{ display: 'block', fontSize: '0.85rem', color: '#64748b', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '0.5rem' }}>UAN Number</span>
              <span className="value" style={{ fontSize: '1.1rem', color: '#0f172a', fontWeight: '500' }}>{employee.uanNumber || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>
      {showVerifyModal && (
        <VerifyPasswordModal
          isOpen={showVerifyModal}
          onCancel={() => setShowVerifyModal(false)}
          onClose={() => setShowVerifyModal(false)}
          onVerified={() => {
            setIsSalaryVisible(true);
            setShowVerifyModal(false);
          }}
          message="Please enter your password to view your salary information."
        />
      )}
    </div>
  );
};

export default EmployeePersonalInfo;
